import { useEffect, useState } from 'react'
import { useI18n } from '../i18n/index.js'
import { Icon, isCrawler, SOCIAL_LINKS } from './Chrome.js'

/** "Beat the machine", the Instagram challenge of November 2026: shown while entries are open (Paris time). */
const CAMPAIGN = {
  id: 'beat-the-machine-2026-11',
  from: Date.parse('2026-11-02T00:00:00+01:00'),
  until: Date.parse('2026-11-30T00:00:00+01:00'),
}
const DISMISSED_KEY = `chess-uno:dismissed:${CAMPAIGN.id}`

export function campaignRunning(now: number): boolean {
  return now >= CAMPAIGN.from && now < CAMPAIGN.until
}

function dismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

function dismiss(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    // Blocked storage: the note comes back on the next visit.
  }
}

/**
 * A small floating note about the challenge, on the pages visitors land on. Floating, so it never
 * shifts the prerendered page; hidden for crawlers and once closed.
 */
export function CampaignNote() {
  const { t } = useI18n()
  const [shown, setShown] = useState(false)
  // While the visitor fills in a field (tapped or typed in, not the page's own autofocus), on a phone.
  const [typing, setTyping] = useState(false)
  useEffect(() => {
    setShown(campaignRunning(Date.now()) && !dismissed() && !isCrawler())
  }, [])
  useEffect(() => {
    if (!shown) return
    const inField = (event: Event) => event.target instanceof Element && event.target.matches('input, textarea, select')
    const start = (event: Event) => inField(event) && setTyping(true)
    const end = (event: Event) => inField(event) && setTyping(false)
    document.addEventListener('pointerdown', start)
    document.addEventListener('input', start)
    document.addEventListener('focusout', end)
    return () => {
      document.removeEventListener('pointerdown', start)
      document.removeEventListener('input', start)
      document.removeEventListener('focusout', end)
    }
  }, [shown])
  if (!shown) return null
  const c = t.campaign
  return (
    <aside className={`campaign-note${typing ? ' is-typing' : ''}`} aria-labelledby="campaign-note-title" data-testid="campaign-note">
      {/* The campaign's mark on Instagram: an enamel card with a brass star. */}
      <svg className="campaign-note-mark" viewBox="0 0 30 42" aria-hidden="true">
        <rect x="1" y="1" width="28" height="40" rx="4" />
        <path d="M15 12c.75 6 3 8.25 9 9-6 .75-8.25 3-9 9-.75-6-3-8.25-9-9 6-.75 8.25-3 9-9Z" />
      </svg>
      <div className="campaign-note-copy">
        <p className="campaign-note-eyebrow">{c.eyebrow}</p>
        <p className="campaign-note-title" id="campaign-note-title" lang="en">
          {c.title}
        </p>
        <p className="campaign-note-text">{c.text}</p>
        <a className="campaign-note-link" href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" data-testid="campaign-note-link">
          <Icon name="instagram" size={14} />
          <span>{c.link}</span>
          <span aria-hidden="true"> ↗</span>
        </a>
      </div>
      <button
        type="button"
        className="ghost icon-btn campaign-note-close"
        aria-label={c.dismiss}
        title={c.dismiss}
        onClick={() => {
          dismiss()
          setShown(false)
        }}
      >
        ✕
      </button>
    </aside>
  )
}
